import { StripeElementsOptions } from "@stripe/stripe-js";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { AddOnItemName, CustomDomainInfo } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import {
  Coupon,
  Invoice,
  StripeCustomer,
} from "components/StripeCheckout/schema";

import { OrganizationUserContext } from "./Organization";
import { SplashPage } from "./Subscription";

export type BundledAddOnItem = {
  name: AddOnItemName;
  quantity: number;
};

interface CheckoutInterface {
  coupon: string | undefined;
  setCoupon: (coupon: string | undefined) => void;
  requiresBillingAddress: boolean;
  setRequiresBillingAddress: (requires: boolean) => void;
  invoice: Invoice | null;
  setInvoice: (invoice: Invoice | null) => void;
  customer: StripeCustomer;
  setCustomer: Dispatch<SetStateAction<StripeCustomer>>;
  promoCode: Coupon;
  setPromoCode: (promoCode: Coupon) => void;
  noNeedsToPay: boolean;
  setNoNeedsToPay: (noNeedsToPay: boolean) => void;
  options: StripeElementsOptions;
  setOptions: (options: StripeElementsOptions) => void;
  addOnItems: BundledAddOnItem[];
  setAddOnItems: (addOnItems: BundledAddOnItem[]) => void;
  plan: AddOnItemName | Plans | null;
  setPlan: (plan: AddOnItemName | Plans | null) => void;
  pageSource: string | null;
  setPageSource: (pageSource: string | null) => void;
  source: string | null;
  setSource: (source: string | null) => void;
  applyAddOnLoading: AddOnItemName | null;
  setApplyAddOnLoading: Dispatch<SetStateAction<AddOnItemName | null>>;
  domainInfo: CustomDomainInfo | null;
  setDomainInfo: Dispatch<SetStateAction<CustomDomainInfo | null>>;
  invoiceLoading: boolean;
  setInvoiceLoading: Dispatch<SetStateAction<boolean>>;
  splashPageSource: SplashPage | null;
  setSplashPageSource: Dispatch<SetStateAction<SplashPage | null>>;
  conciergeOption: number | null;
  setConciergeOption: Dispatch<SetStateAction<number | null>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  coupon: undefined,
  setCoupon: defaultContextMissingFunction,
  requiresBillingAddress: true,
  setRequiresBillingAddress: defaultContextMissingFunction,
  invoice: null,
  setInvoice: defaultContextMissingFunction,
  customer: {
    name: "",
    address: {
      country: "us",
      postal_code: "",
    },
  },
  setCustomer: defaultContextMissingFunction,
  promoCode: {
    id: "",
    name: "",
  },
  setPromoCode: defaultContextMissingFunction,
  noNeedsToPay: false,
  setNoNeedsToPay: defaultContextMissingFunction,
  options: {
    mode: "subscription",
    currency: "usd",
    amount: 0,
  } as StripeElementsOptions,
  setOptions: defaultContextMissingFunction,
  addOnItems: [],
  setAddOnItems: defaultContextMissingFunction,
  plan: null,
  setPlan: defaultContextMissingFunction,
  pageSource: null,
  setPageSource: defaultContextMissingFunction,
  source: null,
  setSource: defaultContextMissingFunction,
  addOns: {},
  setAddOns: defaultContextMissingFunction,
  plans: null,
  setPlans: defaultContextMissingFunction,
  applyAddOnLoading: null,
  setApplyAddOnLoading: defaultContextMissingFunction,
  domainInfo: null,
  setDomainInfo: defaultContextMissingFunction,
  invoiceLoading: false,
  setInvoiceLoading: defaultContextMissingFunction,
  splashPageSource: null,
  setSplashPageSource: defaultContextMissingFunction,
  conciergeOption: null,
  setConciergeOption: defaultContextMissingFunction,
};

const CheckoutContext = createContext<CheckoutInterface>(defaultInterface);

interface CheckoutProviderProps {
  children: React.ReactNode;
}

const CheckoutProvider = ({ children }: CheckoutProviderProps) => {
  const { currentOrg } = useContext(OrganizationUserContext);

  const [coupon, setCoupon] = useState<string | undefined>(undefined);
  const [requiresBillingAddress, setRequiresBillingAddress] = useState(true);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [customer, setCustomer] = useState<StripeCustomer>({
    name: currentOrg?.name || "",
    address: {
      country: "us",
      postal_code: "",
    },
  });
  const [promoCode, setPromoCode] = useState<Coupon>({
    id: "",
    name: "",
  });
  const [noNeedsToPay, setNoNeedsToPay] = useState(false);
  const [options, setOptions] = useState<StripeElementsOptions>({
    mode: "subscription",
    currency: "usd",
    amount: 0,
  });
  const [addOnItems, setAddOnItems] = useState<BundledAddOnItem[]>([]);

  const [plan, setPlan] = useState<AddOnItemName | Plans | null>(null);
  const [pageSource, setPageSource] = useState<string | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [domainInfo, setDomainInfo] = useState<CustomDomainInfo | null>(null);
  const [applyAddOnLoading, setApplyAddOnLoading] =
    useState<AddOnItemName | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [splashPageSource, setSplashPageSource] = useState<SplashPage | null>(
    null,
  );
  const [conciergeOption, setConciergeOption] = useState<number | null>(null);

  return (
    <CheckoutContext.Provider
      value={{
        coupon,
        setCoupon,
        requiresBillingAddress,
        setRequiresBillingAddress,
        invoice,
        setInvoice,
        customer,
        setCustomer,
        promoCode,
        setPromoCode,
        noNeedsToPay,
        setNoNeedsToPay,
        options,
        setOptions,
        addOnItems,
        setAddOnItems,
        plan,
        setPlan,
        pageSource,
        setPageSource,
        source,
        setSource,
        applyAddOnLoading,
        setApplyAddOnLoading,
        domainInfo,
        setDomainInfo,
        invoiceLoading,
        setInvoiceLoading,
        splashPageSource,
        setSplashPageSource,
        conciergeOption,
        setConciergeOption,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export { CheckoutProvider, CheckoutContext };
